import { Vector2 } from '../types';
import { PuzzlePiece } from '../game/PuzzlePiece';

/**
 * 空间索引（基于网格）
 * 用于快速检索特定区域内的碎片，将吸附检测复杂度从 O(N) 降至 O(1)
 */
export class SpatialIndex {
    private grid: Map<string, Set<string>> = new Map();
    private cellSize: number;
    private pieceToCells: Map<string, string[]> = new Map();

    constructor(cellSize: number = 200) {
        this.cellSize = cellSize;
    }

    /**
     * 获取网格 Key
     */
    private getCellKey(x: number, y: number): string {
        const cx = Math.floor(x / this.cellSize);
        const cy = Math.floor(y / this.cellSize);
        return `${cx},${cy}`;
    }

    /**
     * 更新碎片在索引中的位置
     */
    updatePiece(piece: PuzzlePiece): void {
        this.removePiece(piece.id);

        const cells: string[] = [];
        // 考虑碎片的包围盒，可能跨越多个网格
        // 对于拼图碎片，通常只需检查中心点所在的网格
        // 但为了保险，我们可以检查中心点周围的一小块区域
        const key = this.getCellKey(piece.position.x, piece.position.y);

        if (!this.grid.has(key)) {
            this.grid.set(key, new Set());
        }
        this.grid.get(key)!.add(piece.id);
        cells.push(key);

        this.pieceToCells.set(piece.id, cells);
    }

    /**
     * 从索引中移除碎片
     */
    removePiece(pieceId: string): void {
        const cells = this.pieceToCells.get(pieceId);
        if (cells) {
            for (const key of cells) {
                this.grid.get(key)?.delete(pieceId);
            }
            this.pieceToCells.delete(pieceId);
        }
    }

    /**
     * 获取指定位置附近的碎片 ID
     * @param position 中心位置
     * @param radius 搜索半径
     */
    getNearbyPieceIds(position: Vector2, radius: number): string[] {
        const nearbyIds: Set<string> = new Set();

        const startX = Math.floor((position.x - radius) / this.cellSize);
        const endX = Math.floor((position.x + radius) / this.cellSize);
        const startY = Math.floor((position.y - radius) / this.cellSize);
        const endY = Math.floor((position.y + radius) / this.cellSize);

        for (let ix = startX; ix <= endX; ix++) {
            for (let iy = startY; iy <= endY; iy++) {
                const key = `${ix},${iy}`;
                const cell = this.grid.get(key);
                if (cell) {
                    for (const id of cell) {
                        nearbyIds.add(id);
                    }
                }
            }
        }

        return Array.from(nearbyIds);
    }

    /**
     * 清空索引
     */
    clear(): void {
        this.grid.clear();
        this.pieceToCells.clear();
    }
}
